import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  Box,
  IconButton,
  Paper,
  Backdrop,
  alpha,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { VideoCallContext } from 'store/VideoCallContext';
import ringtone1 from '../../assets/img/ringtone.mp3';

// Define animations
const pulseLight = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const wave = keyframes`
  0% { height: 8px; }
  50% { height: 24px; }
  100% { height: 8px; }
`;

const shadowPulse = keyframes`
  0% { box-shadow: 0 0 8px 2px ${alpha('#0017E7', 0.3)}; }
  50% { box-shadow: 0 0 16px 4px ${alpha('#E700C8', 0.5)}; }
  100% { box-shadow: 0 0 8px 2px ${alpha('#0017E7', 0.3)}; }
`;

// Styled components
const PulsingContainer = styled(Box)({
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.73) 100%)',
    animation: `${pulseLight} 2.5s infinite`,
    width: '200%',
    height: '200%',
    zIndex: 0,
    pointerEvents: 'none'
  }
});

const AvatarContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: '30px',
  width: 110,
  height: 110,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    padding: '3px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    animation: `${pulse} 2s ease-in-out infinite`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `2px solid transparent`,
  animation: `${shadowPulse} 4s ease-in-out infinite`,
  fontSize: '36px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  position: 'relative',
  zIndex: 2
}));

const WaveContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  marginBottom: '16px',
  height: '30px'
});

const WaveBar = styled(Box)(({ theme, index }) => ({
  width: '4px',
  height: '12px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '2px',
  animation: `${wave} 1.2s ease-in-out infinite`,
  animationDelay: `${index * 0.15}s`
}));

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  width: '64px',
  height: '64px',
  backgroundColor: color === 'primary' 
    ? alpha(theme.palette.primary.main, 0.2) 
    : alpha(theme.palette.error.main, 0.2),
  '&:hover': {
    backgroundColor: color === 'primary' 
      ? alpha(theme.palette.primary.main, 0.6) 
      : alpha(theme.palette.error.main, 0.6),
    transform: 'translateY(-5px)',
  },
  transition: 'all 0.3s ease',
}));

const IncomingCall = ({ open, onClose, callData }) => {
  const [ringtone, setRingtone] = useState(null);
  const { videoCallLaunched, setVideoCallLaunched } = useContext(VideoCallContext);

  useEffect(() => {
    if (open && callData) {
      const audio = new Audio(ringtone1);
      audio.loop = true;
      audio.play().catch(error => {
        console.log("Error playing ringtone:", error);
      });
      setRingtone(audio);

      if ("Notification" in window) {
        Notification.requestPermission().then(permission => {
          if (permission === "granted" && callData?.sender_info) {
            const notification = new Notification("Incoming Call", {
              body: `${callData.sender_info.name || 'Someone'} is calling you`,
              icon: callData.sender_info.avatar || '/default-avatar.png',
              tag: `call-${callData.sender_info.id}`,
              requireInteraction: true
            });

            notification.onclick = () => {
              window.focus();
            };
          }
        });
      }
    }

    return () => {
      if (ringtone) {
        ringtone.pause();
        ringtone.currentTime = 0;
      }
    };
  }, [open, callData]);

  const handleAcceptCall = () => {
    if (ringtone) {
      ringtone.pause();
      ringtone.currentTime = 0;
    }
    setVideoCallLaunched(true);
    onClose('accept');
  };

  const handleRejectCall = () => {
    if (ringtone) {
      ringtone.pause();
      ringtone.currentTime = 0;
    }
    onClose('reject');
  };

  if (!callData?.sender_info) {
    return null;
  }

  const { sender_info } = callData;

  return (
    <Dialog
      open={Boolean(open)}
      maxWidth="xs"
      fullWidth
      BackdropComponent={(props) => (
        <Backdrop
          {...props}
          sx={{
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            transition: 'backdrop-filter 0.3s, background-color 0.3s',
          }}
        />
      )}
      PaperComponent={(props) => (
        <Paper 
          {...props} 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(16px)',
            borderRadius: 6,
            border: '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden',
            position: 'relative',
          }} 
        />
      )}
      PaperProps={{
        elevation: 24
      }}
    >
      <PulsingContainer />
      <DialogContent sx={{ 
        textAlign: 'center', 
        py: 4, 
        position: 'relative',
        zIndex: 1,
        background: 'transparent'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Avatar with pulse animation */}
          <Box sx={{ position: 'relative', mb: 3, display: 'flex', justifyContent: 'center' }}>
            <AvatarContainer>
              <StyledAvatar 
                src={sender_info.image || '/default-avatar.png'} 
                alt={sender_info.first_name || 'Caller'}
              >
                {sender_info.first_name?.charAt(0) || '?'}
              </StyledAvatar>
            </AvatarContainer>
          </Box>
          
          {/* Call status indicator with wave animation */}
          <Typography sx={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '24px',
            color: '#000000',
            textAlign: 'center',
            marginBottom: '10px',
          }}>
            Incoming Call
          </Typography>
          
          <Typography sx={{
            fontFamily: 'Poppins',
            fontWeight: '400',
            fontSize: '16px',
            color: '#000000',
            textAlign: 'center',
            marginBottom: '10px',
          }}>
            {(sender_info.first_name || 'Someone').charAt(0).toUpperCase() + (sender_info.first_name || 'Someone').slice(1)} 
            {sender_info.last_name ? ` ${sender_info.last_name.charAt(0).toUpperCase() + sender_info.last_name.slice(1)}` : ''}
          </Typography>
          
          <WaveContainer>
            {[0, 1, 2, 3].map((index) => (
              <WaveBar key={index} index={index} />
            ))}
          </WaveContainer>
          
          {/* Call actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%', mt: 2, gap: 3 }}>
            <ActionButton 
              color="error" 
              onClick={handleRejectCall}
              aria-label="Decline call"
            >
              <CallEndIcon fontSize="large" />
            </ActionButton>
            
            <ActionButton 
              color="primary" 
              onClick={handleAcceptCall}
              aria-label="Accept call"
            >
              <CallIcon fontSize="large" />
            </ActionButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

IncomingCall.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  callData: PropTypes.shape({
    sender_info: PropTypes.shape({
      name: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      image: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.string,
      department: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    timestamp: PropTypes.string
  })
};

IncomingCall.defaultProps = {
  open: false,
  callData: null
};

export default IncomingCall;